import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { useSideSheetRegistration } from "./SideSheetContext";
import type { SideSheetProps } from "./types";

export function SideSheetRoot({
  open,
  onClose,
  children,
  className = "",
}: SideSheetProps) {
  const { depth, myIndex } = useSideSheetRegistration(open);

  const behindCount = myIndex >= 0 ? depth - myIndex - 1 : 0;
  const isBehind = behindCount > 0;

  const sheetScale = isBehind ? 0.95 - (behindCount - 1) * 0.03 : 1;
  const sheetShiftX = isBehind ? -24 * behindCount : 0;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content
              asChild
              onPointerDownOutside={() => onClose()}
              onEscapeKeyDown={() => onClose()}
            >
              <motion.div
                className={`fixed top-0 right-0 z-50 h-full w-[740px] max-w-[90vw] bg-surface flex flex-col focus:outline-none border-t-2 border-accent ${
                  isBehind
                    ? "rounded-2xl side-sheet-shadow-behind"
                    : "rounded-l-2xl side-sheet-shadow"
                } ${className}`}
                initial={{ x: "100%" }}
                animate={{
                  x: sheetShiftX,
                  scale: sheetScale,
                }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                {children}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
